import { BasicType } from './../constants';
// TYPE-ONLY on purpose. `@core/typings` does `import { IPage } from '@core/blocks'`
// at runtime, so a value import here would pull in every block — and back
// through @core/utils into block.ts and BlockManager, recreating a cycle.
import type { IBlock, IBlockData } from '@core/typings';

/**
 * Listing-scope predicates and block placement rules.
 *
 * Everything here is a FUNCTION DECLARATION, not a const arrow. These are
 * called from the drag-and-drop hot path (BlockManager.getAutoCompletePath on
 * every dragover, and useBlock.addBlock on every drop), and this package has
 * circular imports between block.ts, BlockManager and the block registry. A
 * const is in the temporal dead zone until its module finishes evaluating, so
 * inside a cycle it throws "X is not defined"; a function declaration is
 * hoisted and safe. That is exactly what took the whole canvas down.
 */

// The Listing Wrapper marks a region of the email as listing-driven.
export const CUSTOM_LISTING_BLOCK_TYPE = 'custom_listing_block';

// The two "listing primitive" sidebar blocks. Dropping one does NOT insert the
// primitive — it opens the Choose-a-Block picker filtered to the saved blocks in
// the matching tags$Category. Those categories are surfaced ONLY through the
// picker, so the host excludes them from the side-panel Block Library.
export const SINGLE_LISTING_BLOCK_TYPE = 'text_single_listing';
export const MULTI_LISTING_BLOCK_TYPE = 'listings_block';

// block type -> tags$Category name the picker offers. NOTE: these strings must
// match the category names used when tagging library blocks; change here if the
// tags differ.
export const LISTING_PICKER_CATEGORY_BY_TYPE: Record<string, string> = {
  [SINGLE_LISTING_BLOCK_TYPE]: 'Single Listing Blocks',
  [MULTI_LISTING_BLOCK_TYPE]: 'Multiple Listings',
};

// Categories the side panel must hide (they live behind the picker instead).
export const LISTING_PICKER_CATEGORIES = Object.values(
  LISTING_PICKER_CATEGORY_BY_TYPE,
);

// Hoisted (see file header) — called from the drop hot path.
export function isListingPickerType(type?: string | null): boolean {
  return Boolean(type && type in LISTING_PICKER_CATEGORY_BY_TYPE);
}

const COLUMN_TYPES = ['column', 'advanced_column'];

/**
 * A column switched to "Listing" — it owns a listing for everything inside it.
 * Columns arrive pre-flagged from the sidebar's "Listing" section variant; the
 * mode is a flag, not a block type, so layout and drag behaviour are unchanged.
 */
export function isDynamicListingColumn(block?: IBlockData | null): boolean {
  if (!block) return false;
  return (
    COLUMN_TYPES.indexOf(block.type) !== -1 &&
    Boolean((block as any)?.data?.value?.isDynamicListing)
  );
}

/** Either thing that can own a listing: the wrapper, or a listing column. */
export function isListingScopeBlock(block?: IBlockData | null): boolean {
  if (!block) return false;
  if (block.type === CUSTOM_LISTING_BLOCK_TYPE) return true;
  return isDynamicListingColumn(block);
}

/**
 * Whether a dragged section payload is a "Listing" section — one whose columns
 * each own a listing. Payload-level rather than type-level: both section
 * variants are the same block type, the difference is the flag on their
 * columns. Used to keep Listing sections out of a Listing Wrapper, where the
 * wrapper's listing already cascades to everything.
 */
export function isListingSectionPayload(
  payload: Partial<IBlockData> | null | undefined
): boolean {
  const children = payload?.children;
  if (!Array.isArray(children)) return false;
  return children.some((child) => isDynamicListingColumn(child as IBlockData));
}

/**
 * The three blocks that pick their own listing(s). They are the alternative to
 * a listing scope, not something you put inside one.
 */
const LISTING_BLOCK_TYPES = [
  'text_single_listing',
  'listings_block',
  'criteria_block',
];

export function isListingBlockType(type?: string | null): boolean {
  if (!type) return false;
  return LISTING_BLOCK_TYPES.indexOf(type) !== -1;
}

/**
 * Whether a listing block is being dropped somewhere a listing already comes
 * from — inside a Listing Wrapper, or inside a listing column. Two listings
 * competing for the same subtree has no sensible answer, so the drop is
 * refused outright.
 *
 * Pure on purpose: the caller resolves the ancestor (it has the tree), this
 * only decides. `hasListingScopeAncestor` should come from
 * `getAncestorCustomListing(content, parentIdx)`, which covers the wrapper and
 * the listing column; `parentBlock` covers the drop target itself.
 *
 * Callers must test the ORIGINAL dragged type, before auto-complete. A Single
 * Listing dropped on a wrapper gets wrapped in section > column on the way in,
 * and by then its type is no longer a listing block.
 */
export function isDeniedListingPlacement(
  dragType: string | null | undefined,
  parentBlock: IBlockData | null | undefined,
  hasListingScopeAncestor: boolean
): boolean {
  if (!isListingBlockType(dragType)) return false;
  if (hasListingScopeAncestor) return true;
  if (!parentBlock) return false;
  return (
    parentBlock.type === CUSTOM_LISTING_BLOCK_TYPE ||
    isDynamicListingColumn(parentBlock)
  );
}

/**
 * Whether a block may be placed inside a given parent type.
 *
 * The Listing Wrapper renders an mj-wrapper, so anything allowed in a Wrapper
 * is allowed in it. Treating it as an alias here — at the single point where
 * placement is decided — beats repeating the type in every block's
 * validParentType, and means blocks added later work in it for free.
 */
export function isValidParentType(
  block: Pick<IBlock, 'validParentType'> | null | undefined,
  parentType: string | null | undefined
): boolean {
  if (!block || !parentType) return false;
  if (block.validParentType.indexOf(parentType) !== -1) return true;
  if (parentType !== CUSTOM_LISTING_BLOCK_TYPE) return false;

  // Only structural blocks may sit DIRECTLY in a wrapper — mj-wrapper holds
  // sections, not content. A block that can also live in a column is content
  // (text, image, button…): it stays "invalid" here so the auto-complete path
  // wraps it in section > column on the way in.
  const isContentBlock = block.validParentType.indexOf(BasicType.COLUMN) !== -1;
  return !isContentBlock && block.validParentType.indexOf(BasicType.WRAPPER) !== -1;
}
